import { ArrowDropDown } from '@mui/icons-material'
import {
    AccordionDetails,
    AccordionSummary,
    Box,
    Accordion as MuiAccordion,
    Typography,
} from '@mui/material'
import { useId, useState } from 'react'

import type { HTMLAttributes, ReactNode, SyntheticEvent } from 'react'

interface AccordionProps extends HTMLAttributes<HTMLElement> {
    /** The node to use as a title for this accordion. */
    label: ReactNode
    /** The icon to use to indicate this accordion's status. */
    expandIconEl?: JSX.Element | null
    /** Whether the accordion should currently be expanded. */
    isExpanded?: boolean
    /** Whether the accordion should be expanded by default. */
    isExpandedByDefault?: boolean
    showBorder?: boolean
}

export function Accordion({
    children,
    expandIconEl = <ArrowDropDown />,
    isExpanded = false,
    isExpandedByDefault = false,
    label = '',
    showBorder = true,
}: AccordionProps) {
    const accordionId = useId()
    const [isCurrentlyExpanded, setIsCurrentlyExpanded] = useState(
        isExpanded || isExpandedByDefault
    )

    const handleChange = (event: SyntheticEvent) => {
        setIsCurrentlyExpanded((wasExpanded) => !wasExpanded)
    }

    return (
        <Box
            sx={{
                borderBottom: showBorder ? 1 : 0,
                borderColor: 'divider',

                '&:last-of-type': {
                    borderBottom: 0,
                    borderBottomLeftRadius: '4px',
                    borderBottomRightRadius: '4px',
                },

                '&:first-of-type': {
                    borderTopLeftRadius: '4px',
                    borderTopRightRadius: '4px',
                },

                '& &': {
                    border: 1,
                    borderColor: 'divider',
                },

                '&:not(:first-of-type)': {
                    borderTop: 0,
                },
            }}
        >
            <MuiAccordion
                defaultExpanded={isExpandedByDefault}
                expanded={isCurrentlyExpanded}
                onChange={handleChange}
                sx={(theme) => ({
                    boxShadow: 'none',
                    /*'&::before': {
                        backgroundColor: theme.palette.grey[300],
                    },*/
                })}
            >
                <AccordionSummary
                    expandIcon={expandIconEl}
                    aria-controls={`${accordionId}-panel-content`}
                    id={`${accordionId}-panel-header`}
                >
                    <Typography component="div" flex="1">
                        {label}
                    </Typography>
                </AccordionSummary>

                <AccordionDetails>{children}</AccordionDetails>
            </MuiAccordion>
        </Box>
    )
}
